import { createContext, useContext, useState, useEffect } from 'react';
import { translations } from '../translations';

const LanguageContext = createContext();

// Detect if user is likely in Tunisia or French-speaking region
const detectDefaultLanguage = () => {
  try {
    // Check timezone for Tunisia
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone === 'Africa/Tunis') {
      return 'fr';
    }
    
    // Check browser language
    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang) {
      if (browserLang.startsWith('de')) return 'de';
      if (browserLang.startsWith('fr')) return 'fr';
      if (browserLang.startsWith('ar')) return 'ar';
    }
    
    // Check for common Tunisian/North African locales
    const languages = navigator.languages || [];
    for (const lang of languages) {
      if (lang.startsWith('de')) return 'de';
      if (lang.startsWith('fr')) return 'fr';
      if (lang.startsWith('ar')) return 'ar';
    }
  } catch (e) {
    console.log('Language detection error:', e);
  }
  
  return 'en'; // Default fallback
};

// Detect pricing region based on timezone + language
// Returns one of: 'tn' (Tunisia), 'gulf' (UAE/Saudi/Gulf), 'eu' (Europe / default)
const detectRegion = (language) => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone === 'Africa/Tunis') return 'tn';
    const gulfTimezones = [
      'Asia/Dubai', 'Asia/Riyadh', 'Asia/Qatar', 'Asia/Kuwait',
      'Asia/Bahrain', 'Asia/Muscat', 'Asia/Aden',
    ];
    if (gulfTimezones.includes(timezone)) return 'gulf';
  } catch (e) {
    // ignore
  }
  if (language === 'ar') return 'gulf';
  return 'eu';
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage first for returning users
    const saved = localStorage.getItem('preferred_language');
    if (saved && ['en', 'fr', 'ar', 'de'].includes(saved)) {
      return saved;
    }
    // Otherwise detect based on location/browser
    return detectDefaultLanguage();
  });
  const [region, setRegion] = useState(() => detectRegion(
    localStorage.getItem('preferred_language') || detectDefaultLanguage()
  ));
  const [isRTL, setIsRTL] = useState(false);

  useEffect(() => {
    const isArabic = language === 'ar';
    setIsRTL(isArabic);
    document.documentElement.dir = isArabic ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    
    // Save language preference
    localStorage.setItem('preferred_language', language);
    
    // Auto-update region whenever language changes (timezone wins, then language)
    setRegion(detectRegion(language));
    
    // Update font family based on language
    if (isArabic) {
      document.body.style.fontFamily = "'Cairo', sans-serif";
    } else {
      document.body.style.fontFamily = "'Outfit', 'Inter', sans-serif";
    }
  }, [language]);

  const t = (path) => {
    const keys = path.split('.');
    let value = translations[language];
    for (const key of keys) {
      value = value?.[key];
    }
    return value || path;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, region, setRegion, isRTL, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
